// «Стратегии» — cum se intră într-o tranzacție, explicat pe desene.
//
// Pagina asta e pentru cineva care abia începe. De aceea desenele sunt **scheme**, nu
// capturi de pe piață: pe un grafic real, formația e amestecată cu zgomot și începătorul
// nu vede unde e de fapt nivelul. Schema arată exact tiparul, cu intrarea, stopul și
// ținta marcate — apoi omul se duce în Тренажёр și îl caută pe grafice adevărate.
//
// Fiecare strategie de aici corespunde unui tip pe care scanerul nostru chiar îl
// găsește, iar butonul de sub ea deschide antrenorul fix pe acel tip. Altfel ar fi o
// lecție despre altceva decât are omul în mână.
//
// Ce **nu** face pagina: nu promite un procent de reușită. Cifra care contează nu e
// „câte tranzacții ies", ci raportul dintre cât riști și cât câștigi — iar asta e
// aritmetică, nu părere, și e explicată la sfârșit.

import { el } from '../core/util.js';
import { icon } from '../ui/icons.js';

const NS = 'http://www.w3.org/2000/svg';

/**
 * Desenează schema unei formații.
 *
 * `bare` sunt [deschidere, maxim, minim, închidere] într-un spaţiu 0–100, unde 100 e
 * sus. Nu sunt preţuri reale şi nici nu trebuie să fie: schema arată forma, iar forma
 * e tot ce trebuie recunoscut pe grafic.
 */
function schema({ bare, nivel, intrare, stop, tinta, etichetaNivel = 'Уровень' }) {
  const W = 460;
  const H = 200;
  const pad = 16;

  // Scara se potriveşte pe date, nu pe intervalul 0–100. Prima variantă folosea scara
  // fixă, iar cum toate valorile stăteau între 36 şi 78, desenul ocupa o treime din
  // înălţime şi lumânările ieşeau minuscule — exact ce nu trebuie într-o schemă a cărei
  // singură treabă e să se vadă bine.
  const toate = [...bare.flat(), nivel, stop, tinta, intrare?.v].filter((v) => v != null);
  const jos = Math.min(...toate);
  const sus = Math.max(...toate);
  const marja = (sus - jos) * 0.12 || 1;
  const y = (v) => pad + (sus + marja - v) / (sus - jos + marja * 2) * (H - pad * 2);
  const pas = (W - 96) / bare.length;
  const x = (i) => 10 + i * pas + pas / 2;

  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('class', 'st-svg');
  const add = (tag, attrs, text) => {
    const n = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
    if (text != null) n.textContent = text;
    svg.append(n);
    return n;
  };

  // Nivelul — linia în jurul căreia se întâmplă totul.
  add('line', { x1: 6, y1: y(nivel), x2: W - 92, y2: y(nivel),
    stroke: 'var(--text-mute)', 'stroke-width': 1.4, 'stroke-dasharray': '5 4' });
  add('text', { x: 8, y: y(nivel) - 6, class: 'st-lbl' }, etichetaNivel);

  // Stopul şi ţinta pornesc **din dreptul intrării**, nu de la marginea din stânga:
  // ele nu existau înainte de a intra în poziţie, iar trase peste tot graficul treceau
  // prin lumânările de dinainte şi se confundau cu nivelul.
  const dela = intrare ? x(intrare.i) - pas * 0.5 : 6;
  if (stop != null) {
    add('line', { x1: dela, y1: y(stop), x2: W - 92, y2: y(stop),
      stroke: 'var(--down)', 'stroke-width': 1.2, 'stroke-dasharray': '3 3', opacity: .85 });
    add('text', { x: W - 88, y: y(stop) + 4, class: 'st-lbl st-red' }, 'Стоп');
  }
  if (tinta != null) {
    add('line', { x1: dela, y1: y(tinta), x2: W - 92, y2: y(tinta),
      stroke: 'var(--up)', 'stroke-width': 1.2, 'stroke-dasharray': '3 3', opacity: .85 });
    add('text', { x: W - 88, y: y(tinta) + 4, class: 'st-lbl st-green' }, 'Цель');
    // Zona de risc şi cea de câştig, umbrite: raportul dintre ele e tot ce contează,
    // iar aşa se vede dintr-o privire că a doua e mai mare.
    add('rect', { x: dela, y: y(Math.max(intrare.v, stop)), width: (W - 92) - dela,
      height: Math.abs(y(stop) - y(intrare.v)), fill: 'var(--down)', opacity: .07 });
    add('rect', { x: dela, y: y(Math.max(intrare.v, tinta)), width: (W - 92) - dela,
      height: Math.abs(y(tinta) - y(intrare.v)), fill: 'var(--up)', opacity: .07 });
  }

  // Lumânările.
  bare.forEach((b, i) => {
    const [o, h, l, c] = b;
    const urca = c >= o;
    const culoare = urca ? 'var(--up)' : 'var(--down)';
    const cx = x(i);
    add('line', { x1: cx, y1: y(h), x2: cx, y2: y(l), stroke: culoare, 'stroke-width': 1.2 });
    const sus = y(Math.max(o, c));
    const jos = y(Math.min(o, c));
    add('rect', { x: cx - pas * 0.3, y: sus, width: pas * 0.6,
      height: Math.max(1.5, jos - sus), fill: culoare, rx: 1 });
  });

  // Săgeata de intrare — locul, nu momentul: momentul e scris în text.
  if (intrare != null) {
    const cx = x(intrare.i);
    const cy = y(intrare.v);
    const sus = intrare.dir === 'long';
    const d = sus
      ? `M${cx} ${cy + 26} L${cx} ${cy + 7} M${cx - 4.5} ${cy + 12} L${cx} ${cy + 6} L${cx + 4.5} ${cy + 12}`
      : `M${cx} ${cy - 26} L${cx} ${cy - 7} M${cx - 4.5} ${cy - 12} L${cx} ${cy - 6} L${cx + 4.5} ${cy - 12}`;
    add('path', { d, stroke: 'var(--accent)', 'stroke-width': 2, fill: 'none',
      'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
    add('text', { x: cx + 8, y: sus ? cy + 24 : cy - 20, class: 'st-lbl st-blue' }, 'Вход');
  }
  return svg;
}

/** Câteva bare de „fond", ca schema să nu pară suspendată în aer. */
const fond = (n, baza, amplitudine) => Array.from({ length: n }, (_, i) => {
  const t = baza + Math.sin(i * 1.7) * amplitudine;
  const o = t - 1.5;
  const c = t + 1.5;
  return [o, Math.max(o, c) + 2, Math.min(o, c) - 2, c];
});

const STRATEGII = [
  {
    id: 'breakout',
    nume: 'Пробой с ретестом',
    subtitlu: 'Самый надёжный вход для новичка',
    ideea: 'Цена долго упиралась в уровень и наконец пробила его. Но входить в сам '
      + 'пробой опасно: половина пробоев ложные. Ждём, пока цена вернётся к уровню '
      + 'сверху и оттолкнётся — старое сопротивление стало поддержкой.',
    schema: {
      bare: [...fond(6, 42, 4), [46, 48, 44, 47], [47, 62, 46, 60], [60, 63, 55, 57],
        [57, 59, 52, 56], [56, 66, 55, 64], [64, 72, 63, 70], [70, 78, 69, 76]],
      nivel: 52, intrare: { i: 10, v: 54, dir: 'long' }, stop: 48, tinta: 78,
      etichetaNivel: 'Сопротивление → поддержка',
    },
    reguli: [
      ['Вход', 'Не на пробойной свече, а на возврате к уровню — когда свеча закрылась '
        + 'выше него во второй раз.'],
      ['Стоп', 'Под уровнем плюс небольшой запас. Если цена вернулась под него — идея '
        + 'не сработала, спорить не с кем.'],
      ['Цель', 'Ближайший уровень выше. Минимум вдвое дальше стопа, иначе сделка не '
        + 'окупает риск.'],
      ['Отмена', 'Пробой был на слабом объёме или цена сразу ушла обратно — сделки нет.'],
    ],
    cauta: 'Объём на пробое от ×1.5 к среднему, уровень с 3+ касаниями.',
  },
  {
    id: 'false_breakout',
    nume: 'Ложный пробой (сбор ликвидности)',
    subtitlu: 'Вход против толпы',
    ideea: 'За уровнем стоят стопы тех, кто уже в позиции. Цена ныряет туда, собирает '
      + 'их и мгновенно возвращается — остаётся длинная тень. Это не пробой, это '
      + 'ловушка, и работать надо в обратную сторону.',
    schema: {
      bare: [...fond(6, 58, 4), [56, 58, 54, 55], [55, 57, 40, 54], [54, 56, 51, 55],
        [55, 58, 53, 57], [57, 62, 56, 61], [61, 66, 60, 65], [65, 70, 64, 69]],
      nivel: 50, intrare: { i: 9, v: 56, dir: 'long' }, stop: 39, tinta: 70,
      etichetaNivel: 'Поддержка (под ней — стопы)',
    },
    reguli: [
      ['Вход', 'Когда свеча закрылась обратно **внутри** уровня. Не во время прокола — '
        + 'тогда ещё неизвестно, вернётся ли цена.'],
      ['Стоп', 'За кончиком тени. Если цена снова уходит туда — это был настоящий пробой.'],
      ['Цель', 'Противоположная граница диапазона.'],
      ['Отмена', 'Свеча закрылась за уровнем, а не вернулась — тогда это пробой, и вы '
        + 'смотрите не в ту сторону.'],
    ],
    cauta: 'Тень к телу от ×2, объём на проколе выше среднего.',
  },
  {
    id: 'bounce',
    nume: 'Отскок в диапазоне',
    subtitlu: 'Когда рынок стоит боком',
    ideea: 'Пока цена ходит между двумя уровнями, она отбивается от них снова и снова. '
      + 'Покупаем у нижней границы, продаём у верхней — и так до тех пор, пока '
      + 'диапазон не сломается.',
    schema: {
      bare: [[70, 72, 66, 67], [67, 69, 60, 62], [62, 64, 52, 54], [54, 56, 47, 53],
        [53, 60, 52, 59], [59, 66, 58, 65], [65, 71, 64, 69], [69, 72, 66, 68],
        [68, 70, 61, 63], [63, 65, 53, 55], [55, 62, 51, 61], [61, 68, 60, 67]],
      nivel: 51, intrare: { i: 10, v: 55, dir: 'long' }, stop: 45, tinta: 71,
      etichetaNivel: 'Нижняя граница',
    },
    reguli: [
      ['Вход', 'У самой границы, после свечи с отбоем — длинная тень в сторону уровня '
        + 'и закрытие обратно.'],
      ['Стоп', 'Сразу за границей. Он маленький — в этом вся выгода такого входа.'],
      ['Цель', 'Противоположная граница. Не жадничать: диапазон когда-нибудь сломается.'],
      ['Отмена', 'Граница пробита с объёмом — диапазона больше нет, ждём новый уровень.'],
    ],
    cauta: 'Уровень с 4+ касаниями, тень к телу от ×1.5.',
  },
  {
    id: 'squeeze',
    nume: 'Сжатие перед движением',
    subtitlu: 'Вход до того, как рынок побежит',
    ideea: 'Свечи становятся всё меньше, рынок замирает у уровня. Так бывает перед '
      + 'выходом: чем дольше сжатие, тем резче движение. Здесь не входят заранее — '
      + 'здесь ставят ордер и ждут.',
    schema: {
      bare: [[40, 52, 36, 48], [48, 56, 44, 46], [46, 52, 42, 50], [50, 53, 46, 48],
        [48, 51, 46, 49], [49, 51, 47, 50], [50, 51, 48, 49], [49, 50, 48, 50],
        [50, 51, 49, 50], [50, 58, 49, 57], [57, 66, 56, 64], [64, 73, 63, 71]],
      nivel: 51, intrare: { i: 9, v: 53, dir: 'long' }, stop: 46, tinta: 73,
      etichetaNivel: 'Граница сжатия',
    },
    reguli: [
      ['Вход', 'Стоп-ордером чуть за границей сжатия — чтобы попасть в движение с '
        + 'самого начала, а не догонять его.'],
      ['Стоп', 'На другой стороне сжатия. Оно узкое, поэтому и стоп маленький.'],
      ['Цель', 'Минимум в 2–3 раза больше ширины сжатия.'],
      ['Отмена', 'Выход произошёл на слабом объёме и сразу заглох — закрывать, не ждать.'],
    ],
    cauta: 'Диапазон меньше 1.8 ATR, ATR падает — рынок сжимается.',
  },
];

/** Ce трейдер проверяет перед каждым входом. Порядок важен: сверху вниз. */
const LISTA = [
  ['Есть ли уровень?', 'Не «мне кажется», а линия, которую цена уже трогала 3+ раза. '
    + 'Один отскок — это случайность.'],
  ['Есть ли объём?', 'Движение без объёма никто не поддерживает. От ×1.5 к среднему.'],
  ['Монета вообще ходит?', 'Если она проходит 0.3% за свечу, брать с неё нечего — '
    + 'комиссия съест всё. Смотрите «Ход/свеча» в «Формациях».'],
  ['Где стоп?', 'Ставится до входа. Если непонятно, где он — значит непонятно, где вы '
    + 'ошиблись, и входить нельзя.'],
  ['Цель вдвое дальше стопа?', 'Если нет — пропускайте. Такая сделка не окупает риск.'],
];

export class StrategiesSection {
  constructor(app) {
    this.app = app;
  }

  mount(host) {
    this.root = el('div', { class: 'st-section' }, [
      el('div', { class: 'st-doc' }, [
        el('div', { class: 'ideas-head' }, [
          el('h2', { class: 'ideas-title', text: 'Стратегии входа' }),
        ]),
        el('p', { class: 'st-intro', text: 'Четыре ситуации, которые повторяются на '
          + 'рынке каждый день. Схемы показывают форму — на реальном графике она будет '
          + 'кривее, но узнаётся так же. Под каждой — кнопка, чтобы сразу потренировать '
          + 'её в тренажёре на настоящей истории.' }),

        ...STRATEGII.map((s, i) => el('section', { class: 'st-card' }, [
          el('div', { class: 'st-head' }, [
            el('span', { class: 'st-num', text: String(i + 1).padStart(2, '0') }),
            el('div', {}, [
              el('h3', { class: 'st-name', text: s.nume }),
              el('span', { class: 'st-sub', text: s.subtitlu }),
            ]),
          ]),
          el('p', { class: 'st-ideea', text: s.ideea }),
          el('div', { class: 'st-plansa' }, [schema(s.schema)]),
          el('div', { class: 'st-reguli' }, s.reguli.map(([k, v]) => el('div', { class: 'st-regula' }, [
            el('span', { class: `st-regula-k ${k === 'Отмена' ? 'anulare' : ''}`, text: k }),
            el('span', { class: 'st-regula-v', text: v }),
          ]))),
          el('div', { class: 'st-jos' }, [
            el('span', { class: 'st-cauta' }, [icon('search', 11), el('span', { text: s.cauta })]),
            el('button', {
              class: 'btn primary st-train',
              onclick: () => { location.href = `/preplay/?pattern=${s.id}`; },
            }, [icon('clockHistory', 12), el('span', { text: 'Потренировать' })]),
          ]),
        ])),

        el('section', { class: 'st-card' }, [
          el('h3', { class: 'st-name', text: 'Проверка перед каждым входом' }),
          el('p', { class: 'st-ideea', text: 'Пять вопросов. Если хоть на один ответ «нет» — '
            + 'сделки нет. Это скучно, и именно поэтому работает.' }),
          el('div', { class: 'st-lista' }, LISTA.map(([q, a], i) => el('div', { class: 'st-punct' }, [
            el('span', { class: 'st-punct-n', text: String(i + 1) }),
            el('div', {}, [
              el('span', { class: 'st-punct-q', text: q }),
              el('span', { class: 'st-punct-a', text: a }),
            ]),
          ]))),
        ]),

        el('section', { class: 'st-card st-math' }, [
          el('h3', { class: 'st-name', text: 'Почему винрейт — не главное' }),
          el('p', { class: 'st-ideea', text: 'Новички гонятся за процентом удачных сделок. '
            + 'Но выигрывает не тот, кто чаще прав, а тот, у кого прибыльные сделки больше '
            + 'убыточных. Это арифметика, а не мнение:' }),
          el('div', { class: 'st-tabel' }, [
            ['Винрейт', 'Риск : прибыль', 'Из 100 сделок'],
            ['70%', '1 : 0.5', '70 × 0.5 − 30 × 1 = <b class="down">−5</b>'],
            ['50%', '1 : 1', '50 − 50 = <b>0</b>'],
            ['40%', '1 : 2', '40 × 2 − 60 × 1 = <b class="up">+20</b>'],
            ['35%', '1 : 3', '35 × 3 − 65 × 1 = <b class="up">+40</b>'],
          ].map((rand, i) => el('div', { class: `st-rand${i ? '' : ' cap'}` },
            rand.map((c) => {
              const n = el('span', { class: 'st-celula' });
              n.innerHTML = c;
              return n;
            })))),
          el('p', { class: 'st-nota', text: 'Трейдер с 40% удачных сделок и целью вдвое '
            + 'дальше стопа зарабатывает. Трейдер с 70% и близкой целью — теряет. '
            + 'Поэтому «цель вдвое дальше стопа» стоит в списке проверок, а обещаний '
            + 'винрейта вы тут не найдёте: его не существует.' }),
        ]),

        el('section', { class: 'st-card st-risc' }, [
          el('h3', { class: 'st-name' }, [icon('warning', 14), el('span', { text: ' Размер позиции' })]),
          el('p', { class: 'st-ideea', text: 'Правило, которое спасает депозит: в одной '
            + 'сделке рискуйте не больше 1–2% счёта. Не «беру на 20% депозита», а «если '
            + 'стоп сработает, потеряю 1%». Считается от расстояния до стопа, а не от '
            + 'размера позиции — при плече это совсем разные вещи.' }),
          el('p', { class: 'st-nota', text: 'При депозите $1000 и стопе в 0.5% от цены '
            + 'позиция может быть и $2000 с плечом — потому что 0.5% от $2000 это те же '
            + '$10, то есть 1% счёта. Тренажёр считает это за вас: откройте сделку и '
            + 'посмотрите, сколько теряете при стопе.' }),
        ]),
      ]),
    ]);
    host.append(this.root);
  }

  unmount() {
    this.root?.remove();
  }
}
